import type { GolfGameFormat } from '@/lib/reference/golf-games';

export type TeamAssignment = 'auto' | 'manual';

interface PlayerRef {
  id: string;
  name: string;
}

interface Props {
  format: GolfGameFormat;
  teamSize: number;
  teamAssignment: TeamAssignment;
  onTeamSizeChange: (size: number) => void;
  onTeamAssignmentChange: (mode: TeamAssignment) => void;
  totalParticipants?: number;
  players?: PlayerRef[];
  manualTeams?: Record<string, number>;
  onManualTeamChange?: (playerId: string, team: number) => void;
}

function teamSizeLabel(n: number): string {
  if (n === 1) return 'Singles';
  if (n === 2) return 'Pairs (2v2)';
  if (n === 3) return 'Teams of 3';
  if (n === 4) return 'Teams of 4';
  return `${n}-person`;
}

function formatHint(formatId: string): string {
  if (formatId === 'scramble') return 'Scrambles can be 2-, 3-, or 4-person teams. Charity events often use 4-person.';
  if (formatId === 'shamble') return 'Shambles work as 2-, 3-, or 4-person teams. 4 is most common.';
  if (['bestball', 'fourball', 'vegas'].includes(formatId)) return 'This format is played in 2-person teams (2v2).';
  if (['nassau', 'match', 'stroke', 'skins'].includes(formatId)) return 'Play as singles or pairs (2v2). Pairs use best-ball scoring per side.';
  return 'Choose how players are grouped for this format.';
}

export function TeamSetupPanel({
  format,
  teamSize,
  teamAssignment,
  onTeamSizeChange,
  onTeamAssignmentChange,
  totalParticipants = 0,
  players = [],
  manualTeams = {},
  onManualTeamChange,
}: Props) {
  const teamCfg = format.teamConfig;
  if (!teamCfg?.isTeamFormat) return null;

  const sizes = teamCfg.supportedTeamSizes?.length
    ? teamCfg.supportedTeamSizes
    : [teamCfg.defaultTeamSize || 2];

  return (
    <div className="rounded-[10px] border border-line bg-paper p-3">
      <div className="text-[11px] font-bold tracking-wide text-navy">TEAM SETUP</div>
      <p className="mt-1 text-[10px] leading-relaxed text-mute">{formatHint(format.id)}</p>

      {sizes.length > 1 && (
        <div className="mt-3">
          <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-mute">FORMAT</div>
          <div className="flex flex-wrap gap-1.5">
            {sizes.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onTeamSizeChange(n)}
                className={`flex-1 min-w-[72px] rounded-lg px-2 py-2.5 text-xs font-bold ${
                  teamSize === n ? 'bg-navy text-cream' : 'border border-line bg-cream text-ink-soft'
                }`}
              >
                {teamSizeLabel(n)}
              </button>
            ))}
          </div>
        </div>
      )}

      {teamSize >= 2 && (
        <div className="mt-3">
          <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-mute">HOW TO ASSIGN</div>
          <div className="flex gap-1.5">
            {(['auto', 'manual'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onTeamAssignmentChange(mode)}
                className={`flex-1 rounded-lg px-2 py-2.5 text-xs font-bold ${
                  teamAssignment === mode ? 'bg-navy text-cream' : 'border border-line bg-cream text-ink-soft'
                }`}
              >
                {mode === 'auto' ? 'Auto-assign' : 'Pick teams'}
              </button>
            ))}
          </div>
          {teamAssignment === 'auto' && totalParticipants > 0 && (
            <p className="mt-2 text-[10px] text-mute">
              {totalParticipants} player{totalParticipants === 1 ? '' : 's'} →{' '}
              <span className="font-semibold text-navy">
                {Math.ceil(totalParticipants / teamSize)} team{Math.ceil(totalParticipants / teamSize) === 1 ? '' : 's'} of {teamSize}
              </span>
            </p>
          )}
          {teamAssignment === 'manual' && players.length > 0 && onManualTeamChange && (
            <div className="mt-2 space-y-1">
              {players.map(({ id, name }) => {
                const current = manualTeams[id] || 1;
                const maxTeam = Math.max(2, Math.ceil(players.length / teamSize));
                return (
                  <div key={id} className="flex items-center gap-2 py-1">
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-ink">{name}</span>
                    <div className="flex gap-1">
                      {Array.from({ length: maxTeam }, (_, i) => i + 1).map((tn) => (
                        <button
                          key={tn}
                          type="button"
                          onClick={() => onManualTeamChange(id, tn)}
                          className={`h-7 w-7 rounded-md text-[11px] font-bold ${
                            current === tn ? 'bg-navy text-cream' : 'border border-line bg-cream text-ink-soft'
                          }`}
                        >
                          {tn}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {teamSize === 1 && totalParticipants > 0 && (
        <p className="mt-2 text-[10px] text-mute">
          Singles play.{' '}
          <span className="font-semibold text-navy">
            {totalParticipants} player{totalParticipants === 1 ? '' : 's'}, each scoring separately.
          </span>
        </p>
      )}
    </div>
  );
}

export function buildTeamAssignments(opts: {
  teamSize: number;
  teamAssignment: TeamAssignment;
  playerIds: string[];
  manualTeams: Record<string, number>;
}): { id: number; players: string[] }[] | null {
  const { teamSize, teamAssignment, playerIds, manualTeams } = opts;
  if (teamSize <= 1 || playerIds.length === 0) return null;

  if (teamAssignment === 'manual') {
    const byTeam: Record<number, string[]> = {};
    playerIds.forEach((pid) => {
      const tn = manualTeams[pid] || 1;
      if (!byTeam[tn]) byTeam[tn] = [];
      byTeam[tn].push(pid);
    });
    return Object.entries(byTeam)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([id, players]) => ({ id: Number(id), players }));
  }

  const teams: { id: number; players: string[] }[] = [];
  for (let i = 0; i < playerIds.length; i += teamSize) {
    teams.push({ id: teams.length + 1, players: playerIds.slice(i, i + teamSize) });
  }
  return teams;
}

export function teamFormatSummary(teamSize: number, teamAssignment: TeamAssignment): string {
  const sizeLabel = teamSizeLabel(teamSize);
  const assignLabel = teamSize >= 2
    ? (teamAssignment === 'auto' ? 'auto-assign' : 'manual teams')
    : 'singles';
  return `Team setup: ${sizeLabel}${teamSize >= 2 ? ` · ${assignLabel}` : ''}.`;
}
